import React, { useEffect, useState } from 'react';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import { uniqueService } from '@utils/library/unique.service';
import cn from 'classnames';
import type { ICountdownTimerProps } from './countdownTimer.interface';
import { countdownTimerService } from './countdownTimer.service';
import styles from './styles-secondary.module.scss';

export interface ICountdownRenderSecondaryProps extends CountdownRenderProps {
  units: ICountdownTimerProps['units'];
  className?: string;
  classes?: {
    item?: string;
    dots?: string;
    wrapper?: string;
  };
  onClick?: () => void;
}

const CountdownTimerRenderSecondary = ({
  units,
  className,
  classes = {},
  days,
  hours,
  minutes,
  seconds,
  onClick,
}: ICountdownRenderSecondaryProps) => {
  const sections = countdownTimerService.getTimeSections({ days, hours, minutes, seconds }, units);

  const items = sections.map((item, idx) => {
    const isLastItem = sections.length - 1 === idx;

    return (
      <div key={idx} className={styles.countdownSecondaryItem}>
        <span className={styles.time}>
          {item.split('').map((partialItem, index) => (
            <span className={cn(classes.item, styles.secondaryCountdownPartialItem)} key={index}>
              {partialItem}
            </span>
          ))}
          {!isLastItem && <span className={cn(classes.dots, styles.secondaryCountdownDots)}>:</span>}
        </span>
      </div>
    );
  });

  return (
    <div
      onClick={onClick}
      className={cn(className, styles.secondaryCountdown, { [styles.clickable]: Boolean(onClick) })}>
      <div className={cn(styles.wrapper, classes.wrapper)}>{items}</div>
    </div>
  );
};

interface ICountdownTimerSecondaryProps
  extends ICountdownTimerProps,
    Pick<ICountdownRenderSecondaryProps, 'classes' | 'className' | 'onClick'> {}

export const CountdownTimerSecondary = ({
  expirationTime,
  className,
  classes,
  units,
  onComplete,
  onClick,
}: ICountdownTimerSecondaryProps) => {
  const [key, setKey] = useState(uniqueService.getUniqueId());

  const getRenderer = (props: CountdownRenderProps) => {
    return (
      <CountdownTimerRenderSecondary
        {...props}
        className={className}
        units={units}
        classes={classes}
        onClick={onClick}
      />
    );
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const syncCountdownWithRealTime = () => {
      const nextSecondStartsIn = 1000 - new Date().getMilliseconds();

      timeoutId = setTimeout(() => {
        setKey(uniqueService.getUniqueId());
      }, nextSecondStartsIn);
    };

    syncCountdownWithRealTime();

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return <Countdown key={key} date={expirationTime} renderer={getRenderer} onComplete={onComplete} />;
};
